import { ContextWindow } from "@asup/context-menu";
import { isEqual } from "lodash";
import { createContext, ReactNode } from "react";
import { EditorV3Content } from "../../classes";
import { EditorV3ContentProps, IEditorV3Line } from "../../classes/interface";
import { useDebounceStack } from "../../hooks";
import { AddLine, RedoBtn, RemoveLine, UndoBtn } from "../icons";
import iconStyles from "../icons/IconStyles.module.css";
import styles from "./WindowView.module.css";
import { WindowViewLine } from "./WindowViewLine";
import { WindowViewAction, windowViewReducer } from "./windowViewReducer";

interface WindowViewProps {
  id: string;
  showWindowView: boolean;
  setShowWindowView: (show: boolean) => void;
  state: EditorV3Content;
  setState: (state: EditorV3Content) => void;
  includeAt?: boolean;
}

export interface WindowViewContextProps {
  id: string;
  lines: IEditorV3Line[];
  dispatch: (action: WindowViewAction) => void;
  editable: boolean;
  contentProps: EditorV3ContentProps;
  includeAt: boolean;
}

export const WindowViewContext = createContext<WindowViewContextProps | null>(null);

/**
 * WindowView component - Modal window for editing TLF (Tables, Listings, and Figures) content
 * This component provides a modal interface for adding and editing dropdown options in the TLF display screen.
 * It implements proper modal isolation to prevent interaction bleed-through from background elements.
 *
 * @param props - The properties for the WindowView component
 * @param props.id - Unique identifier for the window
 * @param props.showWindowView - Boolean controlling modal visibility
 * @param props.setShowWindowView - Function to toggle modal visibility
 * @param props.state - Current EditorV3Content state
 * @param props.setState - Function to update the EditorV3Content state
 * @param props.includeAt - Optional flag to include @ mention functionality
 * @returns ReactNode containing the modal window
 */
export const WindowView = ({
  id,
  showWindowView,
  setShowWindowView,
  state,
  setState,
  includeAt = false,
}: WindowViewProps): ReactNode => {
  const { currentValue, setCurrentValue, forceUpdate, undo, redo, stack, index } = useDebounceStack<
    IEditorV3Line[]
  >({
    value: state.lines,
    /**
     * WindowView - wrapper that renders a draggable/resizable editor window.
     *
     * @param value - Current value used to control window visibility or state.
     */
    setValue: (value: IEditorV3Line[]) => {
      const ret = new EditorV3Content({ lines: value }, state.contentProps);
      if (
        !isEqual(
          ret.lines.map((l) => l.data),
          state.lines.map((l) => l.data),
        )
      )
        setState(ret);
    },
    debounceMilliseconds: -1,
  });

  return (
    <ContextWindow
      id={id}
      visible={showWindowView}
      title={"Editor contents"}
      titleElement={
        <>
          Editor contents
          <UndoBtn
            fill={index > 0 ? "black" : "gray"}
            onClick={() => undo()}
          />
          <RedoBtn
            fill={index < stack.length - 1 ? "black" : "gray"}
            onClick={() => redo()}
          />
        </>
      }
      style={{
        height: "400px",
        width: "700px",
        maxHeight: "80vh",
        maxWidth: "90vw",
      }}
      onClose={() => {
        forceUpdate();
        setShowWindowView(false);
      }}
    >
      <WindowViewContext.Provider
        value={{
          id,
          lines: currentValue,
          /**
           * Handle block actions such as add/remove/move originating from the
           * window control and dispatch to the editor callbacks.
           *
           * @param action - The action identifier describing the block operation.
           */
          dispatch: (action) => {
            const newLines = windowViewReducer(currentValue, action);
            setCurrentValue(newLines);
          },
          editable: true,
          contentProps: state.contentProps,
          includeAt,
        }}
      >
        <div className={styles.windowViewBody}>
          {state.contentProps?.allowNewLine && <AddLine index={0} />}
          {currentValue.map((line, ix) => (
            <div
              key={ix}
              className={iconStyles.relativeDiv}
            >
              <RemoveLine index={ix} />
              <WindowViewLine
                key={ix}
                lineIndex={ix}
              />
              {state.contentProps?.allowNewLine && <AddLine index={ix + 1} />}
            </div>
          ))}
        </div>
      </WindowViewContext.Provider>
    </ContextWindow>
  );
};

WindowView.displayName = "WindowView";
